// src/modules/competitions/pages/JudgeMyEvaluations.tsx
// ✅ VERSIÓN CORREGIDA - TypeScript Strict Mode

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { judgeService } from '../services/judgeService';
import { MyEvaluation } from '../types/judge';
import '../styles/judgeEvaluations.css';

type FilterType = 'all' | 'final' | 'draft';

const JudgeMyEvaluations: React.FC = () => {
  const navigate = useNavigate();

  const [evaluations, setEvaluations] = useState<MyEvaluation[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    loadEvaluations(true);
  }, [filter]);

  const loadEvaluations = async (reset: boolean = false) => {
    try {
      setIsLoading(true);
      
      const currentOffset = reset ? 0 : offset;
      const filters: any = {
        limit: 20,
        offset: currentOffset
      };

      if (filter === 'final') {
        filters.is_final = true;
      } else if (filter === 'draft') {
        filters.is_final = false;
      }

      const response = await judgeService.getMyEvaluations(filters);
      
      // ✅ CORRECCIÓN: Extraer evaluations en una variable para TypeScript
      const evaluationsData = response.data?.evaluations;
      
      if (response.success && evaluationsData) {
        if (reset) {
          setEvaluations(evaluationsData);
        } else {
          setEvaluations(prev => [...prev, ...evaluationsData]);
        }
        
        setHasMore(evaluationsData.length === 20);
        setOffset(currentOffset + evaluationsData.length);
      } else {
        setError(response.message || 'Error al cargar evaluaciones');
        if (reset) {
          setEvaluations([]);
        }
      }
    } catch (err) {
      setError('Error al cargar historial');
      if (reset) {
        setEvaluations([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = () => {
    loadEvaluations(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredEvaluations = evaluations;

  return (
    <div className="judge-my-evaluations-page">
      <Card variant="glass" className="evaluations-header">
        <h1>📊 Mis Evaluaciones</h1>

        {/* Filtros */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todas
          </button>
          <button
            className={`filter-tab ${filter === 'final' ? 'active' : ''}`}
            onClick={() => setFilter('final')}
          >
            Completadas
          </button>
          <button
            className={`filter-tab ${filter === 'draft' ? 'active' : ''}`}
            onClick={() => setFilter('draft')}
          >
            Borradores
          </button>
        </div>
      </Card>

      {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}

      {isLoading && evaluations.length === 0 ? (
        <Loading message="Cargando evaluaciones..." />
      ) : filteredEvaluations.length === 0 ? (
        <Card variant="flat" className="no-evaluations">
          <div className="empty-state">
            <span className="empty-icon">📝</span>
            <h3>No hay evaluaciones</h3>
            <p>
              {filter === 'all' && 'Aún no has realizado ninguna evaluación'}
              {filter === 'final' && 'No tienes evaluaciones completadas'}
              {filter === 'draft' && 'No tienes borradores guardados'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="evaluations-list">
            {filteredEvaluations.map(evaluation => (
              <Card 
                key={evaluation.evaluation_id}
                variant="elevated"
                className="evaluation-card"
              >
                <div className="evaluation-card-header">
                  <div className="evaluation-status">
                    {evaluation.is_final ? (
                      <span className="status-badge status-final">✅ Finalizada</span>
                    ) : (
                      <span className="status-badge status-draft">⏸️ Borrador</span>
                    )}
                  </div>
                  <div className="evaluation-score">
                    <span className="score-value">{evaluation.total_score}</span>
                    <span className="score-label">puntos</span>
                  </div>
                </div>

                <div className="evaluation-card-body">
                  <h3>{evaluation.image_type_name} - {evaluation.participant_code}</h3>
                  
                  <div className="evaluation-meta">
                    <p>
                      <strong>Competencia:</strong> {evaluation.event_title}
                    </p>
                    <p>
                      <strong>Categoría:</strong> {evaluation.category_name}
                    </p>
                    <p>
                      <strong>Nivel:</strong> {evaluation.level}
                    </p>
                    <p>
                      <strong>Fecha:</strong> {formatDate(evaluation.evaluated_at)}
                    </p>
                  </div>
                </div>

                <div className="evaluation-card-actions">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {/* Ver detalles */}}
                  >
                    Ver Detalles
                  </Button>
                  
                  {!evaluation.is_final && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {/* Continuar evaluación */}}
                    >
                      Continuar Evaluación
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="load-more-section">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                loading={isLoading}
                disabled={isLoading}
              >
                Cargar más
              </Button>
            </div>
          )}

          {/* Info */}
          <div className="results-info">
            <p>
              Mostrando {filteredEvaluations.length} evaluación(es)
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default JudgeMyEvaluations;